/**
 * TopNavLiveSlot — the navbar's single slot for time-critical state.
 *
 * The bar used to carry these as five separate chips: a guild badge, a gift
 * button, the LVL badge, a Fuel-Up chip and an XP multiplier. Each is
 * conditional, so together they added several hundred pixels that appeared
 * and disappeared without warning — and the only way the layout could absorb
 * that was to wrap, which is why the mobile bar has a second row whose
 * membership is "whatever did not fit" (_devour-top-nav.scss:26, :174-186).
 *
 * One slot. Fixed width. At most one thing at a time, highest priority first:
 *
 *   1  game tracking    the only one that reflects what you are doing now
 *   2  XP boost         the only one with a hard deadline
 *   3  Fuel-Up run      an unfinished run, with the board's own verb
 *   4  streak at risk   only on the day it can break
 *   5  season week      ambient
 *
 * Whatever loses the slot is still reachable in the player panel, so nothing
 * is hidden — it is demoted.
 *
 * The countdown renders inline at every width. Previously it was
 * position:absolute + display:none under 1360px, revealed on :hover only —
 * so on every phone and tablet the one item defined by a deadline never
 * showed its deadline (_devour-top-nav.scss:446-466).
 *
 * LIVE is a pulsing dot and the word, in the game's colour. Red is reserved
 * for the current-time marker and primary action; a hue that is also the
 * brand mark, the nav underline and the notification badge cannot also mean
 * "happening right now".
 *
 * Anchor: devourplay-anchor/Play/responsive-2026/navbar.html §04
 */

import { ReactElement } from "react";
import { useNavigate } from "react-router";
import { FaBolt, FaDice, FaFire } from "react-icons/fa";
import "./TopNavLiveSlot.scss";

export interface LiveSlotState {
    /** Overwolf/Electron only — the game being tracked right now. */
    tracking?: { name: string; rank?: number } | null;
    /**
     * XP toward the next level — outranks the multiplier (Shelly,
     * 2026-09-13: a player's progress to next level matters more to them
     * than their multiplier; the boost stays in the panel's In Progress).
     */
    xp?: { level: number; gained: number; total: number } | null;
    /** Active XP multiplier and the time left on it. */
    boost?: { value: string; countdown: string } | null;
    /**
     * An open Fuel-Up run. `moves` is availableMoves off the same query the
     * chip already reads — with none banked there is nothing to act on, so
     * the run carries no verb and drops out of slot contention.
     */
    fuelUp?: { isActive: boolean; loop: number; moves: number; toNextMove?: string } | null;
    /** Only when the streak can break today. */
    streakAtRisk?: { days: number; countdown: string } | null;
    /** Ambient — the lowest-priority thing that can hold the slot. */
    season?: { name: string; week: number; totalWeeks: number } | null;
}

interface TopNavLiveSlotProps {
    state: LiveSlotState;
}

export function TopNavLiveSlot({ state }: TopNavLiveSlotProps): ReactElement | null {
    const navigate = useNavigate();
    const { tracking, xp, boost, fuelUp, streakAtRisk, season } = state;

    /* 1 — tracking */
    if (tracking) {
        return (
            <button
                type="button"
                className="dp-live-slot dp-live-slot--tracking"
                onClick={() => navigate("/play")}
                aria-label={`Tracking ${tracking.name}${tracking.rank ? `, rank ${tracking.rank}` : ""}`}
            >
                <span className="dp-live-slot__pulse" aria-hidden="true" />
                <span className="dp-live-slot__text">
                    <span className="dp-live-slot__key">Tracking</span>
                    <span className="dp-live-slot__value">
                        {tracking.name}{tracking.rank ? ` · #${tracking.rank}` : ""}
                    </span>
                </span>
            </button>
        );
    }

    /* 2 — XP to next level. Opens the player panel's home surface. */
    if (xp) {
        return (
            <button
                type="button"
                className="devour-top-nav_navbar_stats_level-badge"
                onClick={() => navigate("/play")}
                aria-label={`Level ${xp.level}, ${xp.gained.toLocaleString()} of ${xp.total.toLocaleString()} XP`}
                title={`Level ${xp.level} - ${xp.gained.toLocaleString()} / ${xp.total.toLocaleString()} XP`}
            >
                <span className="devour-top-nav_navbar_stats_level-tag">LVL</span>
                <span className="devour-top-nav_navbar_stats_level-num">{xp.level}</span>
                <span className="devour-top-nav_navbar_stats_xp-text">
                    {xp.gained.toLocaleString()}<span className="devour-top-nav_navbar_stats_xp-of"> / {xp.total.toLocaleString()}</span>
                </span>
                <div className="devour-top-nav_navbar_stats_xp-bar">
                    <div
                        className="devour-top-nav_navbar_stats_xp-fill"
                        style={{ width: `${Math.min(xp.total > 0 ? xp.gained / xp.total * 100 : 0, 100)}%` }}
                    />
                </div>
            </button>
        );
    }

    /* 3 — XP boost. Countdown inline, at every width. */
    if (boost) {
        return (
            <div
                className="dp-live-slot dp-live-slot--boost"
                role="status"
                aria-label={`${boost.value} XP boost, ${boost.countdown} remaining`}
            >
                <FaBolt className="dp-live-slot__icon" aria-hidden="true" />
                <span className="dp-live-slot__text">
                    <span className="dp-live-slot__key">XP Boost</span>
                    <span className="dp-live-slot__value">{boost.value} multiplier</span>
                </span>
                <span className="dp-live-slot__countdown">{boost.countdown}</span>
            </div>
        );
    }

    /*
     * 3 — Fuel-Up. MOVE is the board's own verb, disabled there on
     * availableMoves <= 0 (GameBoard.tsx:145-152), so the slot uses the same
     * word under the same condition rather than inventing one. "Resume" was
     * wrong on the facts: gs.isActive means the run is running, not paused —
     * and a board that is down has nothing to play, whatever moves are banked.
     */
    if (fuelUp?.isActive && fuelUp.moves > 0) {
        return (
            <button
                type="button"
                className="dp-live-slot dp-live-slot--fuelup"
                onClick={() => navigate("/fuel-up")}
                aria-label={`Fuel-Up Adventure, loop ${fuelUp.loop}, ${fuelUp.moves} moves ready`}
            >
                <FaDice className="dp-live-slot__icon" aria-hidden="true" />
                <span className="dp-live-slot__text">
                    <span className="dp-live-slot__key">Fuel-Up</span>
                    <span className="dp-live-slot__value">
                        Loop {fuelUp.loop} · {fuelUp.moves} move{fuelUp.moves === 1 ? "" : "s"} ready
                    </span>
                </span>
                <span className="dp-live-slot__verb">Move</span>
            </button>
        );
    }

    /* 4 — streak, only on the day it can break */
    if (streakAtRisk) {
        return (
            <button
                type="button"
                className="dp-live-slot dp-live-slot--streak"
                onClick={() => navigate("/play")}
                aria-label={`${streakAtRisk.days}-day streak at risk, ${streakAtRisk.countdown} left`}
            >
                <FaFire className="dp-live-slot__icon" aria-hidden="true" />
                <span className="dp-live-slot__text">
                    <span className="dp-live-slot__key">Streak</span>
                    <span className="dp-live-slot__value">{streakAtRisk.days} days · at risk</span>
                </span>
                <span className="dp-live-slot__countdown">{streakAtRisk.countdown}</span>
            </button>
        );
    }

    /*
     * 5 — season week. This replaces the full-width ribbon that sat above the
     * bar: a band spanning the viewport for a line of text that changes once
     * a week was the most expensive chrome on the page for the least volatile
     * fact.
     */
    if (season) {
        return (
            <button
                type="button"
                className="dp-live-slot dp-live-slot--season"
                onClick={() => navigate("/season-pass")}
                aria-label={`${season.name}, week ${season.week} of ${season.totalWeeks}`}
            >
                <span className="dp-live-slot__text">
                    <span className="dp-live-slot__key">{season.name}</span>
                    <span className="dp-live-slot__value">
                        Week {season.week} of {season.totalWeeks}
                    </span>
                </span>
            </button>
        );
    }

    return null;
}

export default TopNavLiveSlot;
